import { Injectable } from "@angular/core";
import { ColorService } from "./color.service";

declare var swal: any;

const UUID_ROOT_IDENTIFIER_SERVICE = "48c5d828-ac2a-442d-97a3-0c9822b04979";
const ROOT_DEVICE_INFORMATION_SERVICE = "0000180a-0000-1000-8000-00805f9b34fb";
const ROOT_ROBOT_STATE_CHARACTERISTIC = "00008bb6-0000-1000-8000-00805f9b34fb";
const ROOT_UART_SERVICE = "6e400001-b5a3-f393-e0a9-e50e24dcca9e";
const ROOT_TX_CHARACTERISTIC = "6e400002-b5a3-f393-e0a9-e50e24dcca9e";
const ROOT_RX_CHARACTERISTIC = "6e400003-b5a3-f393-e0a9-e50e24dcca9e";

// robot distance 100 is 16 cm
const stepLenght = 1.6;

// standard polynom for crc-8 algorithm
const crc8poly = 0x07;

//boolean if robot reacts to touch commands
const reactToTouch = false;

// BLE protocol: https://github.com/RootRobotics/root-robot-ble-protocol

@Injectable()
export class RootService {
  private device;
  private server;
  private UARTService;
  //TX characteristic for sending msges
  private TXChar;
  //RX characteristic for receiving msges
  private RXChar;
  // ID increasing for msges sent to robot
  private ID;
  //boolean if robot is connected
  private connected;
  //queue with pending msges to robot
  private queue;
  //msg currently/last sent to robot
  private currmsg;
  //boolean if last msg sent to robot finished executing
  private currmsgReceived;
  //boolean if last state before pe was pen up (false) or down (true)
  private penState;
  //boolean if we are in block based environment
  private XLogoBlocks;
  //timestamp root in miliseconds
  private timestamp;
  //current touch zones pressed
  private touchzones;

  constructor() {
    this.device = null;
    this.server = null;
    this.UARTService = null;
    this.TXChar = null;
    this.RXChar = null;
    this.ID = 0;
    this.connected = false;
    this.queue = [];
    this.currmsgReceived = true;
    this.penState = false;
  }

  public connect() {
    //initalize filter to connect to device with UUID
    let options = {
      filters: [{ services: [UUID_ROOT_IDENTIFIER_SERVICE] }],
      // services that have to be in  filter to access them later
      optionalServices: [ROOT_UART_SERVICE]
    };

    this.device = null;
    // request access to device defined in filter (options)
    (<any>navigator).bluetooth
      .requestDevice(options)
      .then(device => {
        console.log("Name: " + device.name);
        this.device = device;
        //listen if device gets disconnected
        this.device.addEventListener(
          "gattserverdisconnected",
          this.onDisconnected
        );
        // connect with device (Root)
        return device.gatt.connect().then(server => {
          this.server = server;
          this.connected = true;
          console.log("> Bluetooth Device connected");
        });
      })
      .then(server => {
        //get UART service
        return this.server.getPrimaryService(ROOT_UART_SERVICE);
      })
      .then(service => {
        this.UARTService = service;
        //get TX characteristic from UART service to send msges to robot
        return service.getCharacteristic(ROOT_TX_CHARACTERISTIC);
      })
      .then(characteristic => {
        //set last msg as received such that first msg can be sent
        this.currmsgReceived = true;
        this.TXChar = characteristic;
      })
      .then(service => {
        //get RX characteristic from UART service to receive msges from robot
        return this.UARTService.getCharacteristic(ROOT_RX_CHARACTERISTIC);
      })
      .then(characteristic => {
        //start notifications to receive msges from RX characteristic
        this.RXChar = characteristic;
        this.RXChar.startNotifications().then(_ => {
          console.log("> Notifications started");
          // event triggered when receiving new msg from robot, add event listener and bind to current environment
          this.RXChar.addEventListener(
            "characteristicvaluechanged",
            this.handleNotifications.bind(this)
          );
        });
        // set pen down, default state in XLogoOnline
        this.pd();
      });
  }

  //disconnect device
  public disconnect() {
    //check if device already disconnected
    if (!this.device) {
      return;
    }
    console.log("Disconnecting from Bluetooth Device...");
    // if connected then disconnect with gatt
    if (this.device.gatt.connected) {
      this.device.gatt.disconnect();

      //reset all device properties to default
      this.device = null;
      this.server = null;
      this.UARTService = null;
      this.TXChar = null;
      this.RXChar = null;
      this.ID = 0;
      this.connected = false;
      this.queue = [];
      this.currmsgReceived = false;
      this.penState = false;
    } else {
      console.log("> Bluetooth Device is already disconnected");
      this.connected = false;
    }
  }

  //clean up variables if device disconnects itself
  private onDisconnected(event) {
    // Object event.target is Bluetooth Device getting disconnected.
    console.log("> Bluetooth Device disconnected");
    this.device = null;
    this.server = null;
    this.UARTService = null;
    this.TXChar = null;
    this.RXChar = null;
    this.ID = 0;
    this.connected = false;
    this.queue = [];
    this.currmsgReceived = false;
    this.penState = false;
  }

  // calculate checksum crc-8 algorithm (devide msg with polynom return crc remainder of this division)
  // takes 20 byte msg calculates checksum with first 19 bytes returns checksum byte
  private checksum(msg) {
    // according to the implementation of pycrc
    //bit-by-bit fast algorithm
    // standard polynom 0x07

    var d;
    var crc = 0x00;
    var bit;
    // current byte
    var c;

    // go through first 19 bytes of the msg
    for (d = 0; d < 19; d++) {
      var i;
      c = msg.getUint8(d);
      for (i = 0x80; i > 0; i = i >> 1) {
        bit = crc & 0x80;
        if (c & i) {
          bit = !bit;
        }
        crc = crc << 1;
        if (bit) {
          crc = crc ^ crc8poly;
        }
      }
      crc = crc & 0xff;
    }
    return crc & 0xff;
  }

  // check if robot is connected
  public isConnected() {
    if (this.device != null) {
      return this.device.gatt.connected;
    }
    return false;
  }

  //set Blocks state
  public setBlocks(isBlocks: Boolean) {
    this.XLogoBlocks = isBlocks;
  }

  // function to increase ID by one, ID in 0 to 255
  private incID() {
    if (this.ID < 255) {
      this.ID = this.ID + 1;
    } else {
      this.ID = 0;
    }
  }

  private addToQueue(msg) {
    //add msg to queue
    this.queue.push(msg);

    // if the last msg was received send next msg, used if this is the first in the queue
    if (this.currmsgReceived) {
      this.sendNextMsg();
    }
  }

  //send next msg in queue
  private sendNextMsg() {
    // check if penState was changed, necessary to check if there is a currmsg because function gets used for the first msg sent
    if (this.currmsg != null) {
      if (this.currmsg.getUint8(0) == 2) {
        if (this.currmsg.getUint8(3) == 0) {
          this.penState = false;
        }
        if (this.currmsg.getUint8(3) == 1) {
          this.penState = true;
        }
      }
    }
    // set current msg as reveived
    this.currmsgReceived = true;

    // if there is another msg in the queue then send next msg
    if (this.queue.length != 0) {
      this.currmsgReceived = false;
      this.currmsg = this.queue.shift();
      this.TXChar.writeValue(this.currmsg);
    }
  }

  //handle recieved notifications from device
  handleNotifications(event) {
    let value = event.target.value;

    //function to check if msg received is correct
    if (value.getUint8(19) != this.checksum(value)) {
      //error in received msg always in color sensor event (until update)
      if (value.getUint8(0) != 0x04) {
        console.log("error in received msg");
      }
    }

    if (reactToTouch) {
      if (value.getUint8(0) == 17 && value.getUint8(1) == 0) {
        // device 17 touch sensors triggered
        //functions to steer with with touch
        this.reactToTouch(value);
        return 1;
      }
    }

    // check if device, command and id is the same as the last msg sent to device (i.e if it is a response)
    if (
      value.getUint8(0) == this.currmsg.getUint8(0) &&
      value.getUint8(1) == this.currmsg.getUint8(1) &&
      value.getUint8(2) == this.currmsg.getUint8(2)
    ) {
      this.sendNextMsg();
      return 1;
    }

    // translate battery level event level drops by more than 10 % device 14 command 0 is event // command 1 would be response
    if (value.getUint8(0) == 14 && value.getUint8(1) == 0) {
      console.log("Battery level at:" + value.getUint8(9));
      return 1;
    }

    // translate bumper event
    if (value.getUint8(0) == 12) {
      if (value.getUint8(7) == 0x00) {
        console.log("no bumper pressed");
      }
      if (value.getUint8(7) == 0x40) {
        console.log("right bumper pressed");
      }
      if (value.getUint8(7) == 0x80) {
        console.log("left bumper pressed");
      }
      if (value.getUint8(7) == 0xc0) {
        console.log("both bumper pressed");
      }
      return 1;
    }

    //read color sensor event
    if (value.getUint8(0) == 4 && value.getUint8(1) == 2) {
      console.log("color sensor event"); //color sensors payload byte 3 to 18 sensor 0 to 31
      return 1;
    }

    //light sensor event
    if (value.getUint8(0) == 13 && value.getUint8(1) == 0) {
      console.log("light sensor triggered");
      if (value.getUint8(7) == 4) {
        console.log("Both eyes dark");
      }
      if (value.getUint8(7) == 5) {
        console.log("Right eye brighter than left eye");
      }
      if (value.getUint8(7) == 6) {
        console.log("Left eye brighter than right eye");
      }
      if (value.getUint8(7) == 7) {
        console.log("Both eyes bright");
      }
      return 1;
    }

    //motor stall event
    if (value.getUint8(0) == 1 && value.getUint8(1) == 29) {
      console.log("Motor stall event");
      // stalled motor: 0 left, 1 right, 2 marker/eraser, cause: 0 no stall, 1 overcurrent, 2 undercurrent, 3 underspeed, 4 saturated PID, 5 timeout
      console.log(
        "stalled motor: " + value.getUint8(7) + " cause: " + value.getUint8(8)
      );
      return 0;
    }
  }

  public moveFd(dist: number) {
    //initialize DataView object
    let arrBuff = new ArrayBuffer(20);
    var msg = new DataView(arrBuff);

    // first byte device 1 motors
    msg.setInt8(0, 0x01);
    // second byte command 8 drive distance
    msg.setInt8(1, 0x08);
    // third byte increasing ID
    msg.setUint8(2, this.ID);
    this.incID();
    //scale distance
    var scaledDist = stepLenght * dist;
    // forth byte payload distance in mm
    msg.setInt32(3, scaledDist);
    // last byte checksum
    msg.setInt8(19, this.checksum(msg));

    //add msg to queue
    this.addToQueue(msg);
  }

  public rotate(angle: number) {
    //initialize DataView object
    let arrBuff = new ArrayBuffer(20);
    var msg = new DataView(arrBuff);

    // first byte device 1 motors
    msg.setInt8(0, 0x01);
    // second byte command 12 rotate angle
    msg.setInt8(1, 0x0c);
    // third byte increasing ID
    msg.setUint8(2, this.ID);
    this.incID();
    //scale angle
    var scaledAngle = angle * 10;
    //forth byte payload angle in 1/10 degree
    msg.setInt32(3, scaledAngle);
    // last byte checksum
    msg.setInt8(19, this.checksum(msg));

    //add msg to queue
    this.addToQueue(msg);
  }

  private setPenErasorState(state) {
    let arrBuff = new ArrayBuffer(20);
    var msg = new DataView(arrBuff);

    // device 2 marker/eraser
    msg.setInt8(0, 0x02);
    // command 0 set marker/eraser position
    msg.setInt8(1, 0x00);
    msg.setUint8(2, this.ID);
    this.incID();
    // position 0: marker/eraser up, 1: marker down/eraser up, 2: marker up/eraser down
    msg.setUint8(3, state);
    msg.setInt8(19, this.checksum(msg));

    //add msg to queue
    this.addToQueue(msg);
  }

  // pen up
  public pu() {
    this.setPenErasorState(0);
  }

  // pen down
  public pd() {
    this.setPenErasorState(1);
  }

  // pen erase
  public pe() {
    this.setPenErasorState(2);
  }

  //pen paint used after pe, restores pen to state before pe
  public ppt() {
    if (this.penState) {
      this.pd();
    } else if (!this.penState) {
      this.pu();
    }
  }

  //empty queue from all pending msges
  public cleanQueue() {
    this.queue = [];
    this.ID = 0;

    // boolean last msg was sent true
    this.currmsgReceived = true;
    this.pd();
  }

  // pen color in XLogoBlocks should function as a way to set the pen down or up
  public setPenColor(color: ColorService) {
    if (this.XLogoBlocks) {
      if (color.isSameAsColor(new ColorService("white"))) {
        this.pe();
      } else {
        this.pd();
      }
    }
  }

  private reactToTouch(value) {
    var bitfield = value.getUint8(7);
    var currtimestamp = value.getUint32(6);

    var timediff = this.timestamp - currtimestamp;
    // react if more than one second since last change
    if (timediff >= 1000) {
      if (this.touchzones == 0xf0) {
        // payload all sensors
        this.sendNextMsg();
      }
      if (this.touchzones == 144 || this.touchzones == 0x80) {
        // payload front and rear left sensors triggered 0b1001 0000
        // rotate left
        this.rotate(-90);
      }
      if (this.touchzones == 96 || this.touchzones == 0x40) {
        // payload front and rear right sensors triggered 0b0110 0000
        // rotate right
        this.rotate(90);
      }
      if (this.touchzones == 192) {
        // both front sensors triggered 0b1100 0000
        // move one unit forward
        this.moveFd(100);
      }
      if (this.touchzones == 48) {
        // both rear sensored triggered 0b0011 0000
        // move one unit back
        this.moveFd(-100);
      }
      if (this.touchzones == 0xa0 || this.touchzones == 0x50) {
        // diagonal eg. front left and back right sensors 0b1010 0000
        if (this.penState) {
          this.pu();
        } else {
          this.pd();
        }
      }
    }
    // save new values for next command
    this.timestamp = currtimestamp;
    this.touchzones = bitfield;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/root.service.ts